import { useMemo } from 'react';
import { useUrlState } from '@/hooks/useUrlState';
import { compact, urlField } from '@/lib/urlState';
import { CPListParams } from '@/types';

const SERVICE_ID_LIMIT = 10;

const CP_SCHEMA = {
  page: urlField.page(),
  sortBy: urlField.enum(
    ['name', 'updatedAt', 'jurisdiction', 'currency'] as const,
    'updatedAt',
  ),
  sortOrder: urlField.enum(['asc', 'desc'] as const, 'desc'),
  term: urlField.string(),
  jurisdiction: urlField.string(),
  currency: urlField.string(),
  isDraft: urlField.enum(['', 'true', 'false'] as const, ''),
  shopEnabled: urlField.boolean(),
  blockTitle: urlField.string(),
  serviceIds: urlField.numberArray(),
};

const CP_SCHEMA_NO_SHOP = {
  ...CP_SCHEMA,
  shopEnabled: { ...CP_SCHEMA.shopEnabled, parse: () => false },
};

export function useCommercialProposalsFilters(canSeeShop: boolean) {
  const [state, setState, { clear, isFiltered }] = useUrlState(
    canSeeShop ? CP_SCHEMA : CP_SCHEMA_NO_SHOP,
    { viewKeys: ['sortBy', 'sortOrder'] },
  );

  const filters = useMemo(
    (): CPListParams =>
      compact({
        page: state.page,
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
        term: state.term || undefined,
        jurisdiction: state.jurisdiction || undefined,
        currency: state.currency || undefined,
        isDraft: state.isDraft === '' ? undefined : state.isDraft === 'true',
        shopEnabled: state.shopEnabled || undefined,
        blockTitle: state.blockTitle.trim() || undefined,
        serviceIds: state.serviceIds.length
          ? state.serviceIds.slice(0, SERVICE_ID_LIMIT)
          : undefined,
      }),
    [state],
  );

  return { state, filters, setState, clearFilters: clear, isFiltered };
}
